/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BladeModel } from 'src/core/components/BladeModel';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { PatternsBasicFilter } from 'src/luis/appShell/buildPane/patternsPage/components/PatternsBasicFilter';
import { FiltersHeader } from 'src/verticals/utterancesFilters/components/FiltersHeader';
import { FiltersSearch } from 'src/verticals/utterancesFilters/components/FiltersSearch';
import { FiltersItem, FilterType, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type PatternsFilterProps = {
    /**
     * Determines whether the blade is open or not
     */
    isOpen: boolean;

    /**
     * Input entities
     */
    entities: FiltersItem[];

    /**
     * Input intents
     */
    intents: FiltersItem[];

    /**
     * Count the number of filtered items
     */
    resultsCount: number;

    /**
     * Determines whether at least one filter is applied or not.
     */
    areFiltersApplied: boolean;

    /**
     * Handler function for dismiss event
     */
    onLastBladeDismiss: () => void;

    /**
     * Handler function for items selection
     */
    onSelectionChange: (item: FiltersItem) => void;

    /**
     * Handler function for clear all button
     */
    onClearAllClick: () => void;
};

const messages = defineMessages({
    entitiesHeader: {
        id: 'patterns.patternsFilter.entitiesHeader',
        defaultMessage: 'Filter by entities'
    },
    intentsHeader: {
        id: 'patterns.patternsFilter.intents',
        defaultMessage: 'Filter by intents'
    }
});

const { styleDiv } = getStylist('Filters');
const Blade = styleDiv('Blade', { padding: 12 });

/**
 * The filter blade parent component that render the filter blades
 */
export const PatternsFilter = (props: PatternsFilterProps) => {
    const { isOpen, resultsCount, onLastBladeDismiss, entities, intents, onClearAllClick, onSelectionChange, areFiltersApplied } = props;
    const { localize } = useLocalization();
    const [bladeId, setBladeId] = React.useState<FilterType>('general');
    const defaultBladeId: FilterType = 'general';

    const clearAllDisabled = !(intents.filter(intent => intent.isChecked).length + entities.filter(entity => entity.isChecked).length);

    const blades = (): BladeModel[] => {
        switch (bladeId) {
            case 'entities':
                return [
                    {
                        content: (
                            <Blade>
                                <FiltersHeader
                                    title={localize(messages.entitiesHeader)}
                                    onClearAllClick={onClearAllClick}
                                    clearAllDisabled={clearAllDisabled}
                                    onDismissClick={() => setBladeId(defaultBladeId)}
                                />

                                <FiltersSearch
                                    items={entities}
                                    onSelectionChange={(change: SelectionChange) => onSelectionChange(change.args as FiltersItem)}
                                />
                            </Blade>
                        ),
                        header: '',
                        key: 'entities_filter'
                    }
                ];
            case 'intents':
                return [
                    {
                        content: (
                            <Blade>
                                <FiltersHeader
                                    title={localize(messages.intentsHeader)}
                                    onClearAllClick={onClearAllClick}
                                    clearAllDisabled={clearAllDisabled}
                                    onDismissClick={() => setBladeId(defaultBladeId)}
                                />
                                <FiltersSearch
                                    items={intents}
                                    onSelectionChange={(change: SelectionChange) => onSelectionChange(change.args as FiltersItem)}
                                />
                            </Blade>
                        ),
                        header: '',
                        key: 'intents_filter'
                    }
                ];
            default:
                return [
                    {
                        content: (
                            <Blade>
                                <PatternsBasicFilter
                                    entities={entities}
                                    intents={intents}
                                    resultsCount={resultsCount}
                                    onOpenBladeClick={setBladeId}
                                    onClearAllClick={onClearAllClick}
                                    onSelectionChange={(change: SelectionChange) => onSelectionChange(change.args as FiltersItem)}
                                    areFiltersApplied={areFiltersApplied}
                                />
                            </Blade>
                        ),
                        header: <></>,
                        key: 'basic_filters'
                    }
                ];
        }
    };

    return <StackBladeHost blades={blades()} isOpen={isOpen} onLastBladeDismissed={onLastBladeDismiss} onDismiss={onLastBladeDismiss} />;
};
